import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App, { preloadRoute } from "./App.tsx";
import "./index.css";
import { installWhatsAppTracking } from "./lib/enquiryAnalytics";
const removeWhatsAppTracking = installWhatsAppTracking();
if (import.meta.hot) import.meta.hot.dispose(removeWhatsAppTracking);

// PostHog is ~100 KB gzip. Loading it with the page put it in the way of the
// hero on mobile, so it now starts once the page has loaded and the main
// thread is idle. It still records the pageview (the URL has not changed).
const startAnalytics = () =>
  import("posthog-js").then(({ default: posthog }) =>
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      defaults: "2025-05-24",
      capture_exceptions: true,
      debug: import.meta.env.MODE === "development",
    }),
  );
if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
  const whenIdle = () =>
    "requestIdleCallback" in window ? requestIdleCallback(startAnalytics, { timeout: 4000 }) : setTimeout(startAnalytics, 1500);
  if (document.readyState === "complete") whenIdle();
  else window.addEventListener("load", whenIdle, { once: true });
}

// Prefetch on intent: hovering, focusing or touching an internal link loads
// that page's chunk, so the click usually lands on code that is already here.
const prefetchLink = (event: Event) => {
  const link = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
  if (!link || link.origin !== window.location.origin || link.target === "_blank") return;
  preloadRoute(link.pathname).catch(() => {});
};
for (const type of ["pointerover", "focusin", "touchstart"]) {
  document.addEventListener(type, prefetchLink, { passive: true, capture: true });
}

// The prerendered HTML is already on screen. Load this URL's page chunk before
// the first render so React replaces it with the same page, never a blank one.
const render = () =>
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
preloadRoute(window.location.pathname).then(render, render);
